/**
 * Shared validators, formatters, and development-only warnings for the
 * Neighbourhood section transformers.
 *
 * Every validation and formatting rule that more than one transformer needs
 * lives here, so the omission behaviour of Requirement 6 is literally the same
 * code on all four tabs.
 */

/**
 * Finite-number-in-range guard. Rejects non-numbers, NaN, ±Infinity, and
 * anything outside the closed range (Requirements 6.1, 10.7).
 */
export const isFiniteInRange = (value: unknown, min: number, max: number): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;

export const HERO_IMAGE_PARAMS = "?w=600&fit=crop&q=80&auto=format";

/** Append the shared optimization params to a neighbourhood hero image URL. */
export const withHeroImageParams = (url: string): string => `${url}${HERO_IMAGE_PARAMS}`;

/**
 * Trimmed-non-empty-string guard. Returns the trimmed string, or `undefined`
 * for non-strings, empty strings, and whitespace-only strings (Requirements 1.8, 1.9, 6.2, 6.3).
 */
export const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Percent formatter (Requirement 2.10). Absolute value, rounded to the nearest
 * integer with halfway values away from zero, digits only.
 *
 * `Math.abs` first, then `Math.round`, gives away-from-zero halfway behaviour:
 * `Math.round(0.5) === 1`, so `-0.5` formats as `1` rather than `0`.
 * `String` of an integer never emits a thousands separator, and the caller has
 * already range-checked the value, so no exponent form is reachable.
 */
export const formatPercent = (value: number): string => String(Math.round(Math.abs(value)));

/**
 * Temperature formatter (Requirements 5.16, 5.17, 5.18). Celsius, one decimal
 * place, trailing `.0` removed. The `°C` suffix is supplied by the Tolgee string,
 * not by this function, so the number stays a pure interpolation parameter
 * (Requirement 9.3).
 *
 * `signed: false` (the default) renders the magnitude, which is what every
 * interpolated statement needs (Requirement 5.18). `signed: true` keeps the sign,
 * which is what the Hero_Card statistic needs (Requirement 5.16), and normalises
 * `-0` to `0` so no statement can ever read `-0°C`.
 */
export const formatCelsius = (value: number, signed = false): string => {
  const source = signed ? value : Math.abs(value);
  const rounded = Math.round(source * 10) / 10;
  const normalised = rounded === 0 ? 0 : rounded;
  return normalised.toFixed(1).replace(/\.0$/, "");
};

/**
 * One decimal place, trailing zero retained (Requirement 3.11). Used only for
 * police officers per 1,000, which has no trailing-`.0` strip rule: `2` renders
 * as `2.0`. The strip is a Climate-only rule and lives in `formatCelsius` alone.
 */
export const formatOneDecimal = (value: number): string => value.toFixed(1);

export type HazardLevel = "low" | "moderate" | "high";

/**
 * Hazard-band string to Badge level (Requirements 5.6, 5.7, 5.8, 5.21).
 * Case-insensitive on the trimmed band. Returns `undefined` for an unrecognised
 * band, which is the caller's signal to omit the Badge and assign no level.
 */
export const hazardBandToLevel = (band: string): HazardLevel | undefined => {
  switch (band.trim().toLowerCase()) {
    case "no hazard":
    case "very low":
    case "low":
      return "low";
    case "medium":
      return "moderate";
    case "high":
    case "very high":
      return "high";
    default:
      return undefined;
  }
};

/**
 * Development-only omission warning (Requirements 10.9, 10.10).
 *
 * `__DEV__` is a webpack `DefinePlugin` global, so in a production build the
 * condition folds to `false` and the whole body is dropped by dead-code
 * elimination — no warning, no log output, and no `console` reference left in
 * the bundle. This is the only side effect any helper or transformer performs.
 */
export const warnOmitted = (omitted: string, fieldPath: string): void => {
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.warn(`[Neighbourhood] omitted ${omitted}: unexpected value at ${fieldPath}`);
  }
};

// ---------------------------------------------------------------------------
// Copy resolution: EN_DEFAULTS, resolveCopy, selectPluralForm, truncateChars
// (Requirements 9.1, 9.2, 9.4, 9.5, 9.6, 9.8, 14.1, 14.2)
// ---------------------------------------------------------------------------

/**
 * Frozen English defaults for every Tolgee key used by the Neighbourhood section.
 * When Tolgee returns an empty or unusable string (key not yet on the platform,
 * missing translation for a language, or a propagation delay), `resolveCopy`
 * interpolates the params into these defaults instead of showing a raw key name
 * or blank space to the user (Requirements 9.4, 9.5).
 */
export const EN_DEFAULTS: Readonly<Record<string, string>> = Object.freeze({
  // ─── Cost of Living ───────────────────────────────────────────────────────────
  "propertypage.components.neighbourhood.costOfLiving.fact.rent.higher":
    "Rent is <{percent}%> higher than the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.fact.rent.cheaper":
    "Rent is <{percent}%> cheaper than the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.fact.rent.cityHigher":
    "Rent is <{percent}%> higher than the {city} average",
  "propertypage.components.neighbourhood.costOfLiving.fact.rent.cityCheaper":
    "Rent is <{percent}%> cheaper than the {city} average",
  "propertypage.components.neighbourhood.costOfLiving.fact.rent.neutral":
    "Rent is largely in line with the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.fact.transport.higher":
    "Transit costs are <{percent}%> higher than the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.fact.transport.lower":
    "Transit costs are <{percent}%> lower than the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.fact.transport.neutral":
    "Transit costs are largely in line with the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.fact.groceries.higher":
    "Grocery prices are <{percent}%> higher than the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.fact.groceries.cheaper":
    "Grocery prices are <{percent}%> cheaper than the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.fact.groceries.neutral":
    "Grocery prices are largely in line with the top cities average",
  "propertypage.components.neighbourhood.costOfLiving.card.label": "Living cost",
  "propertypage.components.neighbourhood.costOfLiving.footer.averageRent": "Average rent",
  "propertypage.components.neighbourhood.costOfLiving.footer.perMonth": "per mo",
  "propertypage.components.neighbourhood.costOfLiving.footer.perWeek": "per wk",
  "propertypage.components.neighbourhood.costOfLiving.hero.label": "Compared to top cities average",
  "propertypage.components.neighbourhood.costOfLiving.hero.label.city":
    "Compared to {city} average",
  "propertypage.components.neighbourhood.costOfLiving.hero.suffix.higher": "higher",
  "propertypage.components.neighbourhood.costOfLiving.hero.suffix.lower": "lower",
  "propertypage.components.neighbourhood.costOfLiving.source": "Numbeo cost-of-living datasets",

  // ─── Safety ───────────────────────────────────────────────────────────────────
  "propertypage.components.neighbourhood.safety.fact.violentCrime.above":
    "Violent crime is <{percent}%> above the top cities average",
  "propertypage.components.neighbourhood.safety.fact.violentCrime.below":
    "Violent crime is <{percent}%> below the top cities average",
  "propertypage.components.neighbourhood.safety.fact.propertyCrime.higher":
    "Property crime is higher than the top cities average",
  "propertypage.components.neighbourhood.safety.fact.propertyCrime.lower":
    "Property crime remains lower than the top cities average",
  "propertypage.components.neighbourhood.safety.fact.policeOfficers":
    "The city reports <{officers}> police officers per 1,000 residents",
  "propertypage.components.neighbourhood.safety.badge.daytime": "Daytime",
  "propertypage.components.neighbourhood.safety.badge.nighttime": "Nighttime",
  "propertypage.components.neighbourhood.safety.badge.value.safe": "Safe",
  "propertypage.components.neighbourhood.safety.badge.value.moderate": "Moderate",
  "propertypage.components.neighbourhood.safety.badge.value.risk": "Risk",
  "propertypage.components.neighbourhood.safety.card.label": "Safety",
  "propertypage.components.neighbourhood.safety.card.stat.safer": "{percent} Safer",
  "propertypage.components.neighbourhood.safety.card.stat.lessSafe": "{percent} Less safe",
  "propertypage.components.neighbourhood.safety.footer.crimeIndex": "Crime index",
  "propertypage.components.neighbourhood.safety.crimeIndex.band.low": "Low",
  "propertypage.components.neighbourhood.safety.hero.label": "Safety Score",
  "propertypage.components.neighbourhood.safety.hero.label.comparison":
    "than the top cities average",
  "propertypage.components.neighbourhood.safety.source":
    "Our World in data and Numbeo safety indexes",

  // ─── Community ────────────────────────────────────────────────────────────────
  "propertypage.components.neighbourhood.community.fact.medianAge":
    "The median age in the neighbourhood is <{medianAge}>.",
  "propertypage.components.neighbourhood.community.fact.universities":
    "{count, plural, one {There is <{count} university> within {distance} of the property.} other {There are <{count} universities> within {distance} of the property.}}",
  "propertypage.components.neighbourhood.community.fact.renters":
    "<{renterPercentage}%> of residents in the area are renters.",
  "propertypage.components.neighbourhood.community.fact.noise":
    "Noise levels are typical for a residential neighbourhood.",
  "propertypage.components.neighbourhood.community.banner.noise.higher":
    "Noise levels are <higher> than in typical urban areas",
  "propertypage.components.neighbourhood.community.banner.noise.lower":
    "Noise levels are <lower> than in typical urban areas",
  "propertypage.components.neighbourhood.community.card.label": "Community",
  "propertypage.components.neighbourhood.community.card.stat.student": "{percent} Students",
  "propertypage.components.neighbourhood.community.card.stat.renter": "{percent} Renters",
  "propertypage.components.neighbourhood.community.card.subtext.student":
    "Share of the local population.",
  "propertypage.components.neighbourhood.community.card.subtext.renter":
    "Share of local residents who rent.",
  "propertypage.components.neighbourhood.community.footer.nearestUniversity": "Nearest University",
  "propertypage.components.neighbourhood.community.hero.label.student": "Student Population",
  "propertypage.components.neighbourhood.community.hero.label.renter": "Renter Population",
  "propertypage.components.neighbourhood.community.source": "Census demographics",
  "propertypage.components.neighbourhood.community.source.us": "US Census ACS demographics",
  "propertypage.components.neighbourhood.community.source.uk": "Nomis, NRS and NISRA records",

  // ─── Climate ──────────────────────────────────────────────────────────────────
  "propertypage.components.neighbourhood.climate.fact.winter.mild":
    "Winters stay mild, averaging <{temp}> in January",
  "propertypage.components.neighbourhood.climate.fact.winter.cold":
    "Winters are cold, averaging <{temp}> in January",
  "propertypage.components.neighbourhood.climate.fact.summer.cooler":
    "Summer temperatures are <{diff}> cooler than the top cities average",
  "propertypage.components.neighbourhood.climate.fact.summer.hotter":
    "Summer temperatures are <{diff}> higher than the top cities average",
  "propertypage.components.neighbourhood.climate.fact.summer.inLine":
    "Summer temperatures are in line with the top cities average",
  "propertypage.components.neighbourhood.climate.fact.daylight.peak":
    "Residents enjoy <{formatted}> of daylight during peak season",
  "propertypage.components.neighbourhood.climate.fact.daylight.winter":
    "Residents get <{formatted}> of daylight during winter months",
  "propertypage.components.neighbourhood.climate.card.label": "Climate",
  "propertypage.components.neighbourhood.climate.footer.seasonalRange": "Seasonal range",
  "propertypage.components.neighbourhood.climate.badge.airQuality": "AQI",
  "propertypage.components.neighbourhood.climate.badge.earthquake": "Earthquake",
  "propertypage.components.neighbourhood.climate.badge.snowfall": "Snowfall",
  "propertypage.components.neighbourhood.climate.badge.value.mild": "Mild",
  "propertypage.components.neighbourhood.climate.badge.value.fair": "Fair",
  "propertypage.components.neighbourhood.climate.badge.value.severe": "Severe",
  "propertypage.components.neighbourhood.climate.hero.label": "Average Annual Temperature",
  "propertypage.components.neighbourhood.climate.source": "Open Meteo and Think Hazard data",

  // ─── Getting Around ───────────────────────────────────────────────────────────
  "propertypage.components.neighbourhood.gettingAround.card.label": "Transit score",
  "propertypage.components.neighbourhood.gettingAround.card.subtext": "Out of 100",
  "propertypage.components.neighbourhood.gettingAround.footer.nearestStop": "Nearest stop",
  "propertypage.components.neighbourhood.gettingAround.hero.label": "Transit Score",
  "propertypage.components.neighbourhood.gettingAround.source":
    "Numbeo & OpenStreetMap location data",
  "propertypage.components.neighbourhood.gettingAround.banner.station": "Nearest station is",
  "propertypage.components.neighbourhood.gettingAround.banner.busStop": "Nearest bus stop is",
  "propertypage.components.neighbourhood.gettingAround.mode.uberLyft": "Uber/Lyft",
  "propertypage.components.neighbourhood.gettingAround.mode.bike": "Bike",
  "propertypage.components.neighbourhood.gettingAround.mode.walk": "Walk",
  "propertypage.components.neighbourhood.gettingAround.mode.drive": "Drive",
  "propertypage.components.neighbourhood.gettingAround.mode.transit": "Transit",
  "propertypage.components.neighbourhood.gettingAround.fact.walkability.higher":
    "Walkability is <higher> than the top cities average",
  "propertypage.components.neighbourhood.gettingAround.fact.walkability.lower":
    "Walkability is <lower> than the top cities average",
  "propertypage.components.neighbourhood.gettingAround.fact.commute.higher":
    "Commute times are <higher> than the top cities average",
  "propertypage.components.neighbourhood.gettingAround.fact.commute.lower":
    "Commute times are <lower> than the top cities average",

  // ─── Section / Tab labels ─────────────────────────────────────────────────────
  "propertypage.components.neighbourhood.source.label": "Source",
  "propertypage.components.neighbourhood.section.heading": "Neighbourhood",
  "propertypage.components.neighbourhood.tab.costOfLiving": "Cost of Living",
  "propertypage.components.neighbourhood.tab.gettingAround": "Getting Around",
  "propertypage.components.neighbourhood.tab.safety": "Safety",
  "propertypage.components.neighbourhood.tab.community": "Community",
  "propertypage.components.neighbourhood.tab.climate": "Climate",
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFunction = (key: string, params?: Record<string, any>, options?: Record<string, any>) => any;

/**
 * Selects the appropriate plural form from an ICU-style plural template.
 * Pattern: "{count, plural, one {...} other {...}}"
 *
 * Uses a brace-depth counter to find branch boundaries.
 * - `one` for count === 1, `other` otherwise
 * - `one` missing → fall back to `other`
 * - `other` missing → fall back to first branch
 * - Template with no plural wrapper → returned unchanged
 *
 * (Requirement 9.8)
 */
export const selectPluralForm = (template: string, count: number): string => {
  // Find the outermost braced content
  let depth = 0;
  let start = -1;
  let end = -1;
  for (let i = 0; i < template.length; i++) {
    if (template[i] === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (template[i] === "}") {
      depth--;
      if (depth === 0) {
        end = i;
        break;
      }
    }
  }

  if (start === -1 || end === -1) return template;

  const inner = template.slice(start + 1, end);

  // Check if this is a plural form: "count, plural, one {...} other {...}"
  const pluralMatch = inner.match(/^\s*\w+\s*,\s*plural\s*,\s*/);
  if (!pluralMatch) return template;

  const branchesStr = inner.slice(pluralMatch[0].length);

  // Parse branches using brace-depth counting
  const branches: Record<string, string> = {};
  let firstBranch: string | undefined;
  let i = 0;

  while (i < branchesStr.length) {
    // Skip whitespace
    while (i < branchesStr.length && /\s/.test(branchesStr[i])) i++;
    if (i >= branchesStr.length) break;

    // Read branch name (e.g., "one", "other")
    let name = "";
    while (i < branchesStr.length && /\w/.test(branchesStr[i])) {
      name += branchesStr[i];
      i++;
    }
    if (!name) break;

    // Skip whitespace and opening brace
    while (i < branchesStr.length && /\s/.test(branchesStr[i])) i++;
    if (i >= branchesStr.length || branchesStr[i] !== "{") break;
    i++; // skip opening brace

    // Read content using brace-depth counting
    let braceDepth = 1;
    let content = "";
    while (i < branchesStr.length && braceDepth > 0) {
      if (branchesStr[i] === "{") braceDepth++;
      else if (branchesStr[i] === "}") {
        braceDepth--;
        if (braceDepth === 0) {
          i++;
          break;
        }
      }
      content += branchesStr[i];
      i++;
    }

    branches[name] = content;
    if (firstBranch === undefined) firstBranch = content;
  }

  const form = count === 1 ? "one" : "other";
  const selected =
    branches[form] ?? branches[form === "one" ? "other" : "one"] ?? firstBranch ?? template;

  return template.slice(0, start) + selected + template.slice(end + 1);
};

/**
 * Resolves copy from Tolgee with fallback to EN_DEFAULTS (Requirements 9.4, 9.5).
 *
 * Six-step behaviour:
 * 1. Stringify all param values (Tolgee expects strings for interpolation)
 * 2. Call t(key, stringifiedParams)
 * 3. Treat result as unusable if: empty, whitespace-only, or equals the key name itself
 * 4. If usable, apply selectPluralForm when a `count` param is present
 * 5. If unusable, fall back to the interpolated EN_DEFAULTS entry
 * 6. Return "" when no default exists
 */
export const resolveCopy = (
  t: TFunction,
  key: string,
  params?: Record<string, string | number>,
): string => {
  const stringParams = params
    ? Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
    : undefined;

  const resolved = t(key, stringParams);
  const trimmed = resolved?.trim();

  if (trimmed && trimmed !== key) {
    // Usable Tolgee result
    if (params && "count" in params) {
      return selectPluralForm(trimmed, Number(params.count));
    }
    return trimmed;
  }

  // Fall back to EN_DEFAULTS
  const template = EN_DEFAULTS[key];
  if (!template) return "";

  // Interpolate params into the template
  let interpolated = template;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      interpolated = interpolated.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }

  // Apply plural selection if count param present
  if (params && "count" in params) {
    return selectPluralForm(interpolated, Number(params.count));
  }

  return interpolated;
};

// ---------------------------------------------------------------------------
// Text truncation — Destination_Pill (Requirements 14.1, 14.2)
// ---------------------------------------------------------------------------

export const DESTINATION_LABEL_MAX_CHARS = 18;
export const ELLIPSIS = "\u2026";

/**
 * Truncates a string to maxChars grapheme-safe characters using Array.from
 * so a surrogate pair is never split (Requirements 14.1, 14.2).
 */
export const truncateChars = (text: string, maxChars: number): string => {
  const chars = Array.from(text);
  if (chars.length <= maxChars) return text;
  return chars.slice(0, maxChars).join("") + ELLIPSIS;
};
